/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  SpendRuleTargetCondition,
  SpendRuleTargetCondition$inboundSchema,
} from "./spendruletargetcondition.js";

/**
 * Rule action: flag (record events only) or block (deny agent traffic on breach).
 */
export const SpendRuleAction = {
  Flag: "flag",
  Block: "block",
} as const;
/**
 * Rule action: flag (record events only) or block (deny agent traffic on breach).
 */
export type SpendRuleAction = ClosedEnum<typeof SpendRuleAction>;

/**
 * UTC calendar window the budget covers.
 */
export const SpendRuleWindowKind = {
  Daily: "daily",
  Weekly: "weekly",
  Monthly: "monthly",
} as const;
/**
 * UTC calendar window the budget covers.
 */
export type SpendRuleWindowKind = ClosedEnum<typeof SpendRuleWindowKind>;

export type SpendRule = {
  /**
   * Rule action: flag (record events only) or block (deny agent traffic on breach).
   */
  action: SpendRuleAction;
  /**
   * When the rule was created.
   */
  createdAt: Date;
  /**
   * Description of what the rule covers. Empty when unset.
   */
  description: string;
  /**
   * Whether the rule is active.
   */
  enabled: boolean;
  /**
   * The budget rule ID. Identifies one immutable version row: edits produce a successor row with a fresh ID.
   */
  id: string;
  /**
   * Per-person budget in USD for one window.
   */
  limitUsd: number;
  /**
   * The rule name.
   */
  name: string;
  /**
   * The organization ID.
   */
  organizationId: string;
  /**
   * CEL boolean expression over actor usage that identifies a budget breach for matched members.
   */
  ruleExpr: string;
  /**
   * URL-safe identifier derived from the name at creation time. Unique per organization and immutable; the rule URN embeds it.
   */
  slug: string;
  target: SpendRuleTargetCondition;
  /**
   * CEL boolean expression over member attributes (email, directory attributes, groups, roles) selecting who the rule applies to.
   */
  targetExpr: string;
  /**
   * When the rule was last updated.
   */
  updatedAt: Date;
  /**
   * Versioned rule URN, e.g. spend_rule:eng-monthly-cap:v3. Pins the exact rule configuration that produced an event.
   */
  urn: string;
  /**
   * Position of this row in its slug lineage; every edit archives the current row and creates version + 1.
   */
  version: number;
  /**
   * Percentage of the limit at which a warning event is emitted.
   */
  warnAtPct: number;
  /**
   * UTC calendar window the budget covers.
   */
  windowKind: SpendRuleWindowKind;
};

/** @internal */
export const SpendRuleAction$inboundSchema: z.ZodMiniEnum<
  typeof SpendRuleAction
> = z.enum(SpendRuleAction);

/** @internal */
export const SpendRuleWindowKind$inboundSchema: z.ZodMiniEnum<
  typeof SpendRuleWindowKind
> = z.enum(SpendRuleWindowKind);

/** @internal */
export const SpendRule$inboundSchema: z.ZodMiniType<SpendRule, unknown> = z
  .pipe(
    z.object({
      action: SpendRuleAction$inboundSchema,
      created_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      description: z.string(),
      enabled: z.boolean(),
      id: z.string(),
      limit_usd: z.number(),
      name: z.string(),
      organization_id: z.string(),
      rule_expr: z.string(),
      slug: z.string(),
      target: SpendRuleTargetCondition$inboundSchema,
      target_expr: z.string(),
      updated_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      urn: z.string(),
      version: z.int(),
      warn_at_pct: z.int(),
      window_kind: SpendRuleWindowKind$inboundSchema,
    }),
    z.transform((v) => {
      return remap$(v, {
        "created_at": "createdAt",
        "limit_usd": "limitUsd",
        "organization_id": "organizationId",
        "rule_expr": "ruleExpr",
        "target_expr": "targetExpr",
        "updated_at": "updatedAt",
        "warn_at_pct": "warnAtPct",
        "window_kind": "windowKind",
      });
    }),
  );

export function spendRuleFromJSON(
  jsonString: string,
): SafeParseResult<SpendRule, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => SpendRule$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'SpendRule' from JSON`,
  );
}
